import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { observer } from 'mobx-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Button, Modal } from 'antd'
import { useWindowSize } from 'flynk.app.web.core.components/helpers'
import PropTypes from 'prop-types'

import { ModalTitle } from '../../components/QuoteBuilder/ModalTitle'
import { ReceptionMusicModalItem } from '../../components/QuoteBuilder/PackageDetails/ReceptionMusicModalItem'
import {
  GIG_LABEL,
  PRIORITY_BAND_SHOW,
  QUOTE_BUILDER_BREAKPOINT,
} from '../../constants'
import { useStores } from '../../helpers/common'

const ReceptionMusicModalContainer = ({ wrapClassName, getContainer }) => {
  const {
    bookingStore: {
      booking,
      getPerformances,
      pauseOtherVideos,
      receptionPerformances,
      selectReceptionPerformance,
      toggleReceptionMusicModalVisible,
      isPrivateEventBooking,
    },
  } = useStores()

  const { isNarrow, windowInnerHeight } = useWindowSize(
    QUOTE_BUILDER_BREAKPOINT
  )

  const [swiperInstance, setSwiperInstance] = useState(null)

  const title = useMemo(() => {
    if (isPrivateEventBooking) {
      return GIG_LABEL.Reception.privateLabel
    }

    if (isNarrow) {
      return 'reception band size'
    }

    return GIG_LABEL.Reception.label
  }, [isPrivateEventBooking, isNarrow])

  useEffect(() => {
    if (receptionPerformances.length === 0) {
      getPerformances()
    }
  })

  const closeButton = (
    <Button key={1} type="text" onClick={toggleReceptionMusicModalVisible}>
      <FontAwesomeIcon icon={['far', 'times']} className="font-awesome-icon" />
    </Button>
  )

  const buttons = [closeButton]

  const arrReceptionMusicModalItem = [...receptionPerformances]
  arrReceptionMusicModalItem.sort((a, b) => a.price - b.price)
  const receptionMusicModalItemRef = useRef([])
  let currentSliderPos = 0
  useEffect(() => {
    receptionMusicModalItemRef.current =
      receptionMusicModalItemRef.current.slice(
        0,
        arrReceptionMusicModalItem.length
      )
  }, [arrReceptionMusicModalItem])

  const performancesOrdering = isNarrow
    ? arrReceptionMusicModalItem.slice(0)
    : arrReceptionMusicModalItem.slice(0).reverse()

  const repositionController = swiper => {
    if (!isNarrow) {
      const activeEl = swiper.slides[swiper.activeIndex]
      const activeItem = activeEl.getElementsByClassName('reception-modal-item')

      if (activeItem && activeItem.length > 0) {
        const pos = activeItem[0].offsetLeft + activeItem[0].offsetWidth + 60
        document.getElementById('swiperController').style.left = `${pos}px`
      }
    }
  }

  const activeItemIndex = performancesOrdering.findIndex(item => item.selected)
  const pagingArrEls = Array.from(
    document.querySelectorAll('#swiperPagination .swiper-pagination-bullet')
  )
  pagingArrEls.forEach((el, index) =>
    el.classList.toggle('current', index === activeItemIndex)
  )

  const swiperSettings = {
    initialSlide: performancesOrdering.reduce((slide, performance, i) => {
      if (
        performance.selected ||
        (slide === null && performance?.numberOfMuso === PRIORITY_BAND_SHOW)
      ) {
        return i
      }

      return slide
    }, null),
    direction: 'horizontal',
    centeredSlides: true,
    slidesPerView: 1,
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets',
      clickable: true,
    },
    navigation: {
      clickable: true,
      nextEl: '.swiper-arrow.swiper-arrow-next',
      prevEl: '.swiper-arrow.swiper-arrow-prev',
    },
    keyboard: {
      enabled: true,
    },
    mousewheel: true,
    onSlideChange: props => {
      receptionMusicModalItemRef.current[currentSliderPos].setShowMoreExpanded(
        true
      )
      // eslint-disable-next-line react/prop-types
      currentSliderPos = props.realIndex
      pauseOtherVideos()
    },
    onInit: swiper => {
      setSwiperInstance(swiper)
      repositionController(swiper)
    },
    onResize: swiper => {
      repositionController(swiper)
    },
    breakpoints: {
      1200: {
        autoHeight: true,
        centeredSlides: true,
        direction: 'vertical',
        slidesPerView: 2,
        spaceBetween: 0,
      },
    },
  }

  return (
    <Modal
      centered
      open
      footer={null}
      closable={false}
      onCancel={toggleReceptionMusicModalVisible}
      maskClosable
      wrapClassName={`${wrapClassName} qb-scroll`}
      className="qb-modal band-modal qd-customize-small-size-popup reception-modal"
      style={{ maxHeight: windowInnerHeight }}
      title={<ModalTitle buttons={buttons} title={title} />}
      getContainer={getContainer}
    >
      <div className="band-modal-content">
        <div className="reception-modal-content qb-scroll">
          {performancesOrdering.length > 0 && (
            <Swiper {...swiperSettings}>
              {
                // the list should be sorted by band size on desktop, then reverse on mobile.
                performancesOrdering.map((performance, i) => (
                  <SwiperSlide key={performance.id}>
                    {({ isActive }) => (
                      <ReceptionMusicModalItem
                        booking={booking}
                        isActive={isActive}
                        performance={performance}
                        isPrivateEventBooking={isPrivateEventBooking}
                        swiperInstance={swiperInstance}
                        ref={el => {
                          receptionMusicModalItemRef.current[i] = el
                        }}
                        selectReceptionPerformance={selectReceptionPerformance}
                        toggleReceptionMusicModalVisible={
                          toggleReceptionMusicModalVisible
                        }
                      />
                    )}
                  </SwiperSlide>
                ))
              }
            </Swiper>
          )}
          <div className="swiper-controller" id="swiperController">
            <div className="swiper-arrow swiper-arrow-prev">
              <FontAwesomeIcon icon={['fas', 'chevron-left']} />
              <FontAwesomeIcon icon={['fas', 'chevron-up']} />
              <span className="swiper-arrow-top">larger band</span>
              <span className="swiper-arrow-left">smaller band</span>
            </div>
            <div className="swiper-pagination" id="swiperPagination">
              paging
            </div>
            <div className="swiper-arrow swiper-arrow-next">
              <span className="swiper-arrow-bottom">smaller band</span>
              <span className="swiper-arrow-right">larger band</span>
              <FontAwesomeIcon icon={['fas', 'chevron-right']} />
              <FontAwesomeIcon icon={['fas', 'chevron-down']} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

ReceptionMusicModalContainer.propTypes = {
  wrapClassName: PropTypes.string,
  getContainer: PropTypes.string,
}

ReceptionMusicModalContainer.defaultProps = {
  wrapClassName: '',
  getContainer: '#qbDetails',
}

export default observer(ReceptionMusicModalContainer)
